import type { EnvironmentReleaseInternal, ReleasePhaseInternal } from '@gonfalon/openapi/types';
import { getEnvironmentReleaseQuery } from '@gonfalon/rest-api/queries/automatedReleases';
import { useQuery } from '@tanstack/react-query';

import { GUARDED_ROLLOUT_POLLING_INTERVAL_MS } from './guarded-rollouts/constants';
import { hasFailedWithoutData } from './internal/guarded-rollouts/hasFailedWithoutData';

export function getEnvironmentReleaseDetailsQuery({
  projectKey,
  flagKey,
  environmentKey,
  releaseId,
}: {
  projectKey: string;
  flagKey: string;
  environmentKey: string;
  releaseId: string;
}) {
  return getEnvironmentReleaseQuery({
    projectKey,
    flagKey,
    environmentKey,
    releaseId,
    apiVersion: 'beta',
    query: { expand: 'metrics' },
  });
}

export function useEnvironmentReleaseDetails({
  projectKey,
  flagKey,
  environmentKey,
  releaseId,
  isReleaseActive,
}: {
  projectKey: string;
  flagKey: string;
  environmentKey: string;
  releaseId: string | undefined;
  isReleaseActive: boolean;
}): { data: EnvironmentReleaseInternal | undefined; isPending: boolean; isError: boolean } {
  const isEnabled = releaseId !== undefined;
  const { data, isPending, isError } = useQuery({
    ...getEnvironmentReleaseDetailsQuery({ projectKey, flagKey, environmentKey, releaseId: releaseId ?? '' }),
    enabled: isEnabled,
    refetchInterval: (query) =>
      isReleaseActive && !hasFailedWithoutData(query.state) ? GUARDED_ROLLOUT_POLLING_INTERVAL_MS : false,
  });

  return { data, isPending: isEnabled && isPending, isError: isEnabled && isError && data === undefined };
}

export function hydrateReleasePhase(
  phase: ReleasePhaseInternal,
  release: EnvironmentReleaseInternal | undefined,
): ReleasePhaseInternal {
  const detailPhase = release?.phases.find((candidate) => candidate.id === phase.id);
  return detailPhase === undefined ? phase : { ...phase, events: detailPhase.events, metrics: detailPhase.metrics };
}
